"use client";

import { FC } from "react";
import { TFreshRecipe } from "@api-clients/subscriptions";
import useWindowSize from "apps/website/hooks/useWindowSize";

import Container from "../../layout/Container/Container";
import Section from "../../layout/Section/Section";
import {
  IRecipeCard,
  RecipeCard,
} from "../../feature/RecipeList/RecipeCard/RecipeCard";
import TextBody from "../../base/Text/TextBody/TextBody";
import TextTitle from "../../base/Text/TextTitle/TextTitle";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import { RecipeList } from "../../feature/RecipeList/RecipeList";
import {
  RecipesCarousel,
} from "../../feature/Carousel/RecipesCarousel/RecipesCarousel";
import Grid from "../../layout/Grid/Grid";
import Column from "../../layout/Column/Column";
import Spacer from "../../layout/Spacer/Spacer";

type RecipeProductTitle = TFreshRecipe;

export interface ISectionRecipesProps {
  title?: string;
  subtitle?: string;
  boxTitle?: string;
  boxSubTitle?: string;
  boxBody?: string;
}

const SINGLE_PROTEIN = "Single protein";
const KEEPS_CATS_FULL = "Keeps cats full";
const FISH_OIL = "Fish oil, for glossy coats";

const factsAndDescriptionPerRecipe: Record<RecipeProductTitle, {
  facts: string[], description: string, imageBadge: string, imageSrc: string }> = {
  CLUCK: { facts: [ SINGLE_PROTEIN ], description: "Pure chicken goodness! All-time fan favourite, beloved by fussy cats.", imageBadge: "100% Chicken", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/fd86be42e7/1-seo_recipe_page_header_cluck.webp" },
  GOBBLE: { facts: [ KEEPS_CATS_FULL ], description: "Fresh turkey, with traces of chicken. A high protein content (18.1%) keeps them happy and satisfied between mealtimes.", imageBadge: "Turkey + Chicken", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/9a2bf0af4c/1-seo_recipe_page_header_gobble.webp" },
  MOOO: { facts: [ "Iron + Vitamin A", FISH_OIL ], description: "Only the highest-quality beef. Packed full of iron and vitamins for your cat.", imageBadge: "100% Beef", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/747eb56e5a/1-seo_recipe_page_header_mooo.webp" },
  OINK: { facts: [ SINGLE_PROTEIN ], description: "Go the whole hog with fresh pork trim, shoulder and liver.", imageBadge: "100% Pork", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/0be990978d/1-seo_recipe_page_header_oink.webp" },
  QUACK: { facts: [ FISH_OIL, KEEPS_CATS_FULL ], description: "Gently-cooked duck with traces of chicken. With a high protein content (18.8%) to keep them fuller for longer.", imageBadge: "Duck + Chicken", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/09d5451d60/1-seo_recipe_page_header_quack.webp" },
  SPLASH: { facts: [ FISH_OIL, KEEPS_CATS_FULL ], description: "Fresh, 100% gently-cooked whitefish, salmon, and turkey! Purr-fect for felines who fancy fish.", imageBadge: "Whitefish + Salmon + Turkey", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/1d3a6fb15b/1-seo_recipe_page_header_splash.webp" },
  BAAA: { facts: [ SINGLE_PROTEIN, FISH_OIL ], description: "For cats who don't follow the herd. No mainstream fluff - just lamb heart, trim, liver and kidney", imageBadge: "100% Lamb", imageSrc: "https://a.storyblok.com/f/176726/1500x1500/6f6657572d/1-seo_recipe_page_header_baaa.webp" },
  // NB these are placeholder values for shredded and Swish until we want to add them to the lps
  SHREDDED_CLUCK: { facts: [ SINGLE_PROTEIN ], description: "", imageBadge: "100% Chicken", imageSrc: "" },
  SHREDDED_GOBBLE: { facts: [ KEEPS_CATS_FULL ], description: "", imageBadge: "Turkey + Chicken", imageSrc: "" },
  SWISH: { facts: [ "Iron + Vitamin A", KEEPS_CATS_FULL ], description: "", imageBadge: "Tuna + Salmon + Chicken", imageSrc: "" },
};

// Hardcoded not to include shredded recipes and Swish until we want to add them to the lps
const visibleRecipeOrder: RecipeProductTitle[] = [ "CLUCK", "GOBBLE", "MOOO", "OINK", "QUACK", "SPLASH", "BAAA" ];

const recipes: IRecipeCard[] = visibleRecipeOrder.map((recipe) => ({
  description: factsAndDescriptionPerRecipe[recipe].description,
  image: {
    src: factsAndDescriptionPerRecipe[recipe].imageSrc,
    width: 0,
    height: 0,
  },
  titleTag: {
    label: recipe,
  },
  imageBadge: {
    label: factsAndDescriptionPerRecipe[recipe].imageBadge,
    styleOptions: { bgColor: "brand" },
  },
  badgeList: factsAndDescriptionPerRecipe[recipe].facts.map((fact) => ({ label: fact, icon: "tickHeavy", styleOptions: { bgColor: "mid-green" } })),
  styleOptions: { bgColor: "light", imageScale: 110 },
}));

const SectionRecipes: FC<ISectionRecipesProps> = ({ title, subtitle, boxTitle, boxSubTitle, boxBody }) => {
  const { isActiveBreakpointAbove } = useWindowSize();

  const isDesktop = isActiveBreakpointAbove("lg");

  return (
    <Section component={SectionRecipes.name} className="px-4">
      <Container className="mb-6">
        <TextTitle size={1} align="center" className="normal-case mb-4">
          { title }
        </TextTitle>
        <TextBody size={1} align="center">
          { subtitle }
        </TextBody>
      </Container>
      <div data-component={RecipeList.name}>
        { isDesktop ? (
          <Grid gap="lg" className="h-full mx-24" data-testid="recipe-list">
            { recipes.map((recipe, index) => (
              <Column
                key={recipe.titleTag.label}
                spans={3}
                offset={index >= 4 && index === 4 ? 2 : undefined}
                className="h-full"
              >
                <RecipeCard {...recipe} styleOptions={{ bgColor: "light", hasShadow: true }} />
              </Column>
            )) }
          </Grid>
        ) : (
          <RecipesCarousel recipes={recipes} styleOptions={{ slideBgColor: "light", carouselBgColor: "light", hasShadow: true }} />
        ) }
      </div>
      <Container className="bg-light-grey py-4 px-3 mt-6 flex flex-col align-middle justify-around lg:w-1/2">
        <TextSubtitle align="center" size={1}>
          { boxTitle }
        </TextSubtitle>
        <TextBody size={1} align="center" className="my-4">
          { boxSubTitle }
        </TextBody>
        <TextSubtitle align="center" size={2} className="bg-black text-white p-2">
          { boxBody }
        </TextSubtitle>
      </Container>
      <Spacer size="xl" />
    </Section>
  );
};

export default SectionRecipes;
